import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Switch,
  makeStyles,
  Divider,
  AccordionActions,
  Button,
  TextField,
  InputAdornment
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/Settings';
import SendIcon from '@material-ui/icons/Send';
import flespiDeviceService from '../../../../services/FlespiDeviceService';
import toast from 'react-hot-toast';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: '#757575',
    fontWeight: '700'
  },
  accordionSummaryDiv: {
    display: 'flex',
    alignItems: 'center'
  },
  accordionDetailsItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1rem',
    marginTop: '1rem'
  },
  settingsDiv: {
    marginRight: '12px'
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

const BLECommonSettings = ({ currentRecord }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: {
      ble_conn_control: currentRecord[0]?.current?.ble_conn_control,
      ble_service_id: currentRecord[0]?.current?.ble_service_id,
      power_level: currentRecord[0]?.current?.power_level,
      scan_duration: currentRecord[0]?.current?.scan_duration,
      scan_retries: currentRecord[0]?.current?.scan_retries
    }
  });

  const handleBLECommonSettings = (data) => {
    setLoading(true);

    let bleCommonSettingsPayload = {
      deviceId: currentRecord[0]?.device_id,
      settSelector: 'ble_common',
      properties: {
        ble_conn_control: data.ble_conn_control,
        ble_service_id: data.ble_service_id,
        power_level: data.power_level,
        scan_duration: data.scan_duration,
        scan_retries: data.scan_retries,
        non_stop: { scan: false }
      }
    };

    flespiDeviceService
      .updateFlespiDeviceSetting(bleCommonSettingsPayload)
      .then((data) => {
        // eslint-disable-next-line no-console, no-undef
        console.log('data is:', data);
        toast.success(`Successfully updated BLE common settings settings`);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console, no-undef
        console.log('error', error?.response?.data?.message);
        toast.error(error?.response?.data?.message || `Unable to update BLE common settings`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // eslint-disable-next-line no-console, no-undef
  console.log('errors', errors);

  return (
    <div style={{ marginBottom: '1rem' }}>
      <form onSubmit={handleSubmit(handleBLECommonSettings)}>
        <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            className={classes.accordionSummary}
          >
            <div className={classes.accordionSummaryDiv}>
              <div className={classes.settingsDiv}>
                <SettingsIcon />
              </div>

              <div>
                <Typography className={classes.heading}> BLE common settings</Typography>
              </div>
            </div>
          </AccordionSummary>

          <Divider />

          <AccordionDetails className={classes.accordionDetails}>
            <Typography style={{ color: '#9e9e9e', marginBottom: '10px' }}> BLE common settings</Typography>

            {/* scan_duration */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="scan_duration"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="BLE Scan duration"
                    name="scan_duration"
                    size="small"
                    type="number"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.scan_duration}
                    helperText={errors.scan_duration?.message}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Seconds</InputAdornment>
                    }}
                  />
                )}
              />
            </div>

            {/* scan_retries */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="scan_retries"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Scan retries until error"
                    name="scan_retries"
                    size="small"
                    type="number"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.scan_retries}
                    helperText={errors.scan_retries?.message}
                  />
                )}
              />
            </div>

            {/* power_level */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="power_level"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Scan retries until error"
                    name="power_level"
                    size="small"
                    type="number"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.power_level}
                    helperText={errors.power_level?.message}
                  />
                )}
              />
            </div>

            {/* ble_service_id */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="ble_service_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="BLE broadcasting service ID"
                    name="ble_service_id"
                    size="small"
                    type="number"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.ble_service_id}
                    helperText={errors.ble_service_id?.message}
                  />
                )}
              />
            </div>

            {/* ble_conn_control  */}
            <div style={{ display: 'flex', marginTop: '1rem' }}>
              <Controller
                control={control}
                name="ble_conn_control"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="primary"
                    name="ble_conn_control"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                )}
              />

              <Typography style={{ display: 'flex', alignItems: 'center' }}>BLE connection control</Typography>
            </div>
          </AccordionDetails>

          <AccordionActions style={{ cursor: 'pointer' }}>
            <Button
              size="small"
              color="primary"
              title="Changes will apply to the device"
              type="submit"
              disabled={loading}
            >
              <SendIcon />
            </Button>
          </AccordionActions>
        </Accordion>
      </form>
    </div>
  );
};

export default BLECommonSettings;

BLECommonSettings.propTypes = {
  currentRecord: PropTypes.array
};
